import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can, canSeeMyCalendar, type Me } from '../auth/me';
import { useMe } from '../auth/useMe';
import './schedule.css';
import { TaskMeta } from './TasksPanel';
import { SCHEDULE_KEY, splitTasks, TASKS_RESOURCE, type Task } from './types';

type MyTasks = { person: { id: string; name: string } | null; tasks: Task[] };
export const MY_TASKS_KEY = [...SCHEDULE_KEY, 'me'] as const;

/**
 * For the menu: whether the account is linked with a person (then "Mój kalendarz" shows even without grants).
 * Asked only when the permissions do not show the entry anyway; shares the cache with "Do zrobienia".
 */
export function useLinkedPerson(me: Me | null | undefined): boolean {
  const granted = !!me && canSeeMyCalendar(me);
  const q = useQuery({ queryKey: MY_TASKS_KEY, enabled: !!me && !granted, staleTime: 60_000, retry: false, queryFn: () => api<MyTasks>('/api/me/tasks') });
  return !granted && !!q.data?.person;
}

/**
 * "Do zrobienia" (a tab of "Mój kalendarz"): my tasks from every event, open first (overdue in red), ticked off
 * with one tap on the phone; those done in the last days below. Ticking one's own task needs no grant. For roles
 * that see tasks, also the company's rental returns with their suppliers.
 */
export function MyTasksPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const mine = useQuery({ queryKey: MY_TASKS_KEY, queryFn: () => api<MyTasks>('/api/me/tasks') });
  const [ticking, setTicking] = useState<Record<string, boolean>>({});
  const tick = useMutation({
    mutationFn: (t: Task) => api<Task>(`/api/me/tasks/${t.id}/done`, { method: 'POST', body: { done: !t.done } }),
    onSettled: async (_d, _e, t) => {
      await queryClient.invalidateQueries({ queryKey: SCHEDULE_KEY });
      setTicking(({ [t.id]: _, ...rest }) => rest);
    },
  });
  const items = (mine.data?.tasks ?? []).map((t) => (t.id in ticking ? { ...t, done: ticking[t.id]!, overdue: !ticking[t.id] && !!t.overdue } : t));
  const { open, done } = splitTasks(items);

  const row = (t: Task) => (
    <li key={t.id} className={`task-row${t.done ? ' done' : ''}`}>
      <label className="check">
        <input
          type="checkbox"
          checked={!!t.done}
          disabled={tick.isPending && tick.variables?.id === t.id}
          aria-label={`Zrobione: ${t.title ?? 'zadanie'}`}
          onChange={() => {
            setTicking((s) => ({ ...s, [t.id]: !t.done }));
            tick.mutate(t);
          }}
        />
        <span className="task-main">
          <span className="task-title">{t.title ?? 'Zadanie'}</span>
          <TaskMeta task={t} showEvent showPerson={false} />
        </span>
      </label>
    </li>
  );

  return (
    <div className="my-tasks" data-feature-id="schedule.my_tasks">
      {mine.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {mine.error && <p className="alert" role="alert">{mine.error instanceof ApiError ? mine.error.message : 'Nie udało się wczytać zadań'}</p>}
      {tick.error && <p className="alert" role="alert">{tick.error instanceof ApiError ? tick.error.message : 'Nie udało się zapisać'}</p>}
      {mine.data && mine.data.person === null && (
        <p className="notice">Twoje konto nie jest jeszcze połączone z osobą z listy „Osoby”, więc nie masz zadań. Poproś właściciela o połączenie (Osoby → Konto w aplikacji).</p>
      )}
      {mine.data?.person && (
        <section className="my-tasks-section" aria-labelledby="my-tasks-title">
          <h2 id="my-tasks-title">Do zrobienia</h2>
          {open.length === 0 ? <p className="muted">Nie masz nic do zrobienia.</p> : <ul className="task-list" aria-label="Moje zadania do zrobienia">{open.map(row)}</ul>}
          {done.length > 0 && (
            <details className="task-done">
              <summary>Zrobione ostatnio ({done.length})</summary>
              <ul className="task-list" aria-label="Moje zadania zrobione">
                {done.map(row)}
              </ul>
            </details>
          )}
        </section>
      )}
      {me && can(me, TASKS_RESOURCE, 'view') && <Returns />}
    </div>
  );
}

/** Rental returns to suppliers (open first), for the roles that see tasks; scope "own" shows only one's own. */
function Returns() {
  const returns = useQuery({ queryKey: [...SCHEDULE_KEY, 'returns'], queryFn: () => api<{ returns: Task[] }>('/api/schedule/returns') });
  const { open, done } = splitTasks(returns.data?.returns ?? []);
  if (!returns.data || returns.data.returns.length === 0) return null;
  const row = (t: Task) => (
    <li key={t.id} className={`task-row${t.done ? ' done' : ''}`}>
      <span className="task-main">
        <span className="task-title">
          {t.contractorName ? `${t.contractorName}: ` : ''}
          {t.title ?? 'Zwrot'}
        </span>
        <TaskMeta task={{ ...t, kind: 'zwykle' }} showEvent />
      </span>
    </li>
  );
  return (
    <section className="my-tasks-section" aria-labelledby="returns-title" data-feature-id="schedule.returns">
      <h2 id="returns-title">Zwroty wypożyczeń</h2>
      {open.length === 0 ? <p className="muted">Wszystko oddane.</p> : <ul className="task-list" aria-label="Zwroty do zrobienia">{open.map(row)}</ul>}
      {done.length > 0 && (
        <details className="task-done">
          <summary>Oddane ostatnio ({done.length})</summary>
          <ul className="task-list">{done.map(row)}</ul>
        </details>
      )}
    </section>
  );
}
